import React from "react";

import {
  ArrowRight,
  FileText,
  LogIn,
  Mail,
  MailOpen,
  MailX,
  TrendingUp,
  UserCheck,
} from "lucide-react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";

const PILL_BASE =
  "inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium";

export interface AssignmentDetailsRow {
  fullItem?: {
    assignment_request?: {
      assigned_at?: string;
      assigned_by?: string;
      approved_at?: string;
      approved_by?: string;
    };
  };
  loginStatus?: {
    login_status?: string;
    last_login?: string;
    user_account_exists?: string;
    needs_temp_password?: boolean;
    email_sent?: string;
    email_opened?: string;
  };
  lastLogin?: string;
}

interface AssignmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRow: AssignmentDetailsRow | null;
}

const Field = ({
  label,
  value,
  fallback = "N/A",
}: {
  label: string;
  value: string | undefined;
  fallback?: string;
}) => (
  <div className="flex flex-col min-w-0">
    <span className="text-xs text-gray-500 mb-1">{label}</span>
    <span className="text-sm font-medium text-gray-900 break-words">
      {value || fallback}
    </span>
  </div>
);

const StatusPill = ({
  isPositive,
  icon: Icon,
  children,
}: {
  isPositive: boolean;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) => (
  <span
    className={`${PILL_BASE} ${isPositive ? "bg-green-600 text-white" : "bg-gray-400 text-white"}`}
  >
    <Icon className="w-3.5 h-3.5" />
    {children}
  </span>
);

const AssignmentDetailsModal: React.FC<AssignmentDetailsModalProps> = ({
  isOpen,
  onClose,
  selectedRow,
}) => {
  const assignmentRequest = selectedRow?.fullItem?.assignment_request;
  const loginStatus = selectedRow?.loginStatus;
  const lastLogin =
    selectedRow?.lastLogin || loginStatus?.last_login || "Never";

  const emailSent = loginStatus?.email_sent ?? "Sent";
  const emailOpened = loginStatus?.email_opened ?? "Opened";
  const userExists = loginStatus?.user_account_exists ?? "Exists";
  const isLoggedIn = loginStatus?.login_status === "Logged In";
  const needsTempPw = loginStatus?.needs_temp_password ?? false;

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      title="Assignment Details"
      size="4xl"
      centered
    >
      <div className="max-h-[70vh] overflow-y-auto">
        {/* Assignment Information */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
            <FileText className="w-5 h-5 text-purple-600 flex-shrink-0" />
            Assignment Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Assigned On" value={assignmentRequest?.assigned_at} />
            <Field label="Assigned By" value={assignmentRequest?.assigned_by} />
            <Field label="Approved On" value={assignmentRequest?.approved_at} />
            <Field label="Approved By" value={assignmentRequest?.approved_by} />
          </div>
        </div>

        {/* Login & Status Information */}
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
            <TrendingUp className="w-5 h-5 text-orange-500 flex-shrink-0" />
            Login & Status Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-500 mb-1">Login Status</span>
              <span className="inline-flex items-center gap-1.5 w-fit mt-0.5">
                <span
                  className={`${PILL_BASE} text-white ${
                    isLoggedIn ? "bg-green-600" : "bg-red-600"
                  }`}
                >
                  {isLoggedIn ? (
                    <ArrowRight className="w-3.5 h-3.5" />
                  ) : (
                    <LogIn className="w-3.5 h-3.5" />
                  )}
                  {loginStatus?.login_status ?? "Logged In"}
                </span>
              </span>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-500 mb-1">Last Login</span>
              <span className="text-sm font-medium text-gray-900 mt-0.5">
                {lastLogin}
              </span>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-500 mb-1">Email Sent</span>
              <span className="inline-flex items-center gap-1.5 w-fit mt-0.5">
                <StatusPill isPositive={emailSent === "Sent"} icon={Mail}>
                  {emailSent}
                </StatusPill>
              </span>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-500 mb-1">Email Opened</span>
              <span className="inline-flex items-center gap-1.5 w-fit mt-0.5">
                {emailOpened === "Opened" ? (
                  <StatusPill isPositive icon={MailOpen}>
                    Opened
                  </StatusPill>
                ) : (
                  <StatusPill isPositive={false} icon={MailX}>
                    Not Opened
                  </StatusPill>
                )}
              </span>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-500 mb-1">
                User Account Exists
              </span>
              <span className="inline-flex items-center gap-1.5 w-fit mt-0.5">
                <StatusPill
                  isPositive={userExists === "Exists"}
                  icon={UserCheck}
                >
                  {userExists}
                </StatusPill>
              </span>
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-500 mb-1">
                Needs Temp Password
              </span>
              <span className="inline-flex items-center gap-1.5 w-fit mt-0.5">
                <span
                  className={`${PILL_BASE} ${
                    needsTempPw
                      ? "bg-amber-400 text-amber-900"
                      : "bg-green-600 text-white"
                  }`}
                >
                  {needsTempPw ? "Yes" : "No"}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </CommonModal>
  );
};

export default AssignmentDetailsModal;
